'use client';

import React, { useState } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Lock, Shield, Activity, Check } from 'lucide-react';

const modes = [
    {
        id: 'manual',
        label: 'Manual',
        icon: Lock,
        dot: 'bg-neutral-400',
        title: 'Full control. You approve every action.',
        description: 'Axist proposes strategies, content, and ad changes. Nothing executes without your click. Perfect for understanding the system before trusting it fully.',
        feed: [
            { dot: 'bg-neutral-400', text: 'Scout found 482 high-intent leads', tag: 'Awaiting approval', tagColor: 'text-neutral-500 bg-neutral-50 border-neutral-200' },
            { dot: 'bg-neutral-400', text: 'Quill drafted 3 content variations', tag: 'Review needed', tagColor: 'text-neutral-500 bg-neutral-50 border-neutral-200' },
            { dot: 'bg-neutral-400', text: 'Trader suggests scaling Ad #12', tag: 'Approve to run', tagColor: 'text-neutral-500 bg-neutral-50 border-neutral-200' },
        ]
    },
    {
        id: 'copilot',
        label: 'Copilot',
        icon: Shield,
        dot: 'bg-blue-500',
        title: 'AI runs safe actions. You approve spend.',
        description: 'Content, outreach, and lead scoring run fully autonomously. Budget changes pause for approval. A daily digest keeps you in the loop on everything.',
        feed: [
            { dot: 'bg-emerald-500', text: 'Quill published 12 LinkedIn posts', tag: 'Auto-executed', tagColor: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
            { dot: 'bg-blue-400', text: 'Trader: Reallocate $200 budget?', tag: 'Needs approval', tagColor: 'text-blue-600 bg-blue-50 border-blue-100' },
            { dot: 'bg-emerald-500', text: 'Archer deployed outreach to 50 leads', tag: 'Auto-executed', tagColor: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
        ]
    },
    {
        id: 'autopilot',
        label: 'Autopilot',
        icon: Activity,
        dot: 'bg-emerald-500',
        title: 'Your self-evolving AI marketing agents.',
        description: 'Every agent runs fully autonomously — 24 hours a day, 7 days a week. The Brain discovers new opportunities, learns from what works, and reallocates effort without you lifting a finger.',
        feed: [
            { dot: 'bg-emerald-500', text: 'Quill published 3 posts via LinkedIn', tag: 'Auto-executed', tagColor: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
            { dot: 'bg-emerald-500', text: 'Scout discovered 28 hot leads', tag: 'Auto-executed', tagColor: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
            { dot: 'bg-emerald-500', text: 'Archer sent 50 cold outreach DMs', tag: 'Auto-executed', tagColor: 'text-emerald-600 bg-emerald-50 border-emerald-100' },
        ]
    },
];

export const AutopilotSection = () => {
    const [active, setActive] = useState<'manual' | 'copilot' | 'autopilot'>('autopilot');
    const mode = modes.find(m => m.id === active)!;

    return (
        <section className="border-t border-neutral-100 py-24 bg-white">
            <div className="max-w-5xl mx-auto px-6">
                {/* Header */}
                <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-14"
                >
                    <div className="flex items-center gap-3 mb-6">
                        <span className="text-sm font-mono text-neutral-400">7.0</span>
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-neutral-600 uppercase tracking-widest">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Autonomy
                        </span>
                    </div>
                    <h2 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-5 tracking-tight leading-tight max-w-2xl">
                        You choose how much<br />
                        <span className="text-neutral-400">the AI takes over.</span>
                    </h2>
                    <p className="text-lg text-neutral-500 leading-relaxed max-w-xl">
                        Start with full control. Graduate to autopilot. Every mode is fully reversible at any time.
                    </p>
                </m.div>

                {/* Mode toggle — Linear button group style */}
                <div className="flex items-center gap-1 p-1 bg-neutral-100 rounded-xl w-fit mb-10">
                    {modes.map((m) => {
                        const Icon = m.icon;
                        const isActive = active === m.id;
                        return (
                            <button
                                key={m.id}
                                onClick={() => setActive(m.id as any)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
                                    isActive
                                        ? 'bg-white text-neutral-900 shadow-sm border border-neutral-200'
                                        : 'text-neutral-500 hover:text-neutral-700'
                                }`}
                            >
                                <Icon size={14} />
                                {m.label}
                            </button>
                        );
                    })}
                </div>

                {/* Mode content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                    {/* Text */}
                    <AnimatePresence mode="wait">
                        <m.div
                            key={active + '-text'}
                            initial={{ opacity: 0, x: -12 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 12 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex items-center gap-2 mb-5">
                                <span className={`w-2 h-2 rounded-full ${mode.dot}`} />
                                <span className="text-sm text-neutral-500 font-medium">{mode.label} mode active</span>
                            </div>
                            <h3 className="text-2xl font-bold text-neutral-900 mb-4 tracking-tight leading-snug">
                                {mode.title}
                            </h3>
                            <p className="text-neutral-500 leading-relaxed mb-8">
                                {mode.description}
                            </p>
                            {active === 'autopilot' && (
                                <ul className="space-y-2.5">
                                    {['Learns & retires failing patterns', 'Discovers new tool capabilities', 'Competitive market monitoring', 'Revenue attribution closed-loop'].map(item => (
                                        <li key={item} className="flex items-center gap-2.5 text-sm text-neutral-600">
                                            <Check size={14} className="text-emerald-500 shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </m.div>
                    </AnimatePresence>

                    {/* Preview card */}
                    <AnimatePresence mode="wait">
                        <m.div
                            key={active + '-card'}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.3 }}
                            className="rounded-2xl border border-neutral-200 bg-white shadow-[0_4px_30px_rgba(0,0,0,0.06)] overflow-hidden"
                        >
                            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
                                <span className="text-sm font-semibold text-neutral-700">Mission Control</span>
                                <span className="flex items-center gap-1.5 text-[11px] font-medium text-neutral-400">
                                    <span className={`w-1.5 h-1.5 rounded-full ${mode.dot}`} />
                                    {mode.label}
                                </span>
                            </div>
                            <div className="p-4 space-y-2.5">
                                {mode.feed.map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 p-3.5 rounded-xl bg-neutral-50 border border-neutral-100">
                                        <span className={`w-2 h-2 rounded-full ${item.dot} shrink-0`} />
                                        <span className="text-[12px] text-neutral-600 flex-1">{item.text}</span>
                                        <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full ${item.tagColor}`}>
                                            {item.tag}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </m.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};
